import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, BarChart3, Calendar, User, Award, Building } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { apiClient } from '../lib/api/client';

interface CandidateSubmission {
  submission_id: string;
  candidate_id: string;
  requirement_id: string;
  fitment_score: number;
  strengths: string[];
  gaps: string[];
  submission_status: string;
  submission_created_at: string;
  submission_updated_at: string;
  submitted_by: string;
  submitted_by_name: string;
  name: string;
  email: string;
  phone: string;
  location: string;
  experience_years: string;
  current_position: string;
  current_company: string;
  education: string;
  skills: string[];
  cv_path: string;
  candidate_created_at: string;
  candidate_updated_at: string;
}

export default function CandidateSubmissionsPage() {
  const { candidateId } = useParams<{ candidateId: string }>();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);

  // Fetch candidate submissions
  const { data, isLoading, error } = useQuery({
    queryKey: ['candidateSubmissions', candidateId, currentPage, pageSize],
    queryFn: () => apiClient.getCandidateSubmissions(candidateId!, currentPage, pageSize),
    enabled: !!candidateId,
  });

  const submissions = data?.submissions || [];
  const total = data?.total || 0;
  const totalPages = data?.total_pages || Math.ceil(total / pageSize);
  
  // Get candidate name from the first submission or use a fallback
  const candidateName = submissions.length > 0 
    ? (submissions[0].candidate_name || submissions[0].email || submissions[0].phone || 'Unknown Candidate')
    : 'Unknown Candidate';

  const handleViewAnalysis = (candidateId: string, requirementId: string) => {
    navigate(`/candidates/${candidateId}/analysis/${requirementId}`);
  };

  const handleBackToCandidates = () => {
    navigate('/candidates');
  };

  const formatDateTime = (iso?: string) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso as string;
    }
  };

  const getFitmentScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-400 bg-green-500/20 border-green-500/30';
    if (score >= 60) return 'text-yellow-400 bg-yellow-500/20 border-yellow-500/30';
    return 'text-red-400 bg-red-500/20 border-red-500/30';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 mb-4">Failed to load candidate submissions</div>
        <Button onClick={() => window.location.reload()}>
          Try Again
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={handleBackToCandidates}
            className="p-2 hover:bg-glass-surface/50 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-text-enterprise-primary">
              {candidateName} - Submissions
            </h1>
            <p className="text-text-enterprise-secondary">
              View all submissions for this candidate
            </p>
          </div>
        </div>
      </div>

      {/* Submissions Table */}
      <div className="relative overflow-hidden rounded-2xl border border-glass-border/50 shadow-glass-lg bg-gradient-to-br from-glass-surface/80 via-glass-surface/90 to-glass-surface/80 backdrop-blur-xl">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-20 -right-20 w-40 h-40 bg-gradient-to-br from-primary-500/20 to-secondary-500/15 rounded-full blur-2xl opacity-30 animate-pulse"></div>
          <div className="absolute -bottom-20 -left-20 w-32 h-32 bg-gradient-to-br from-secondary-500/20 to-accent-500/15 rounded-full blur-2xl opacity-25 animate-pulse delay-1000"></div>
        </div>

        {submissions.length === 0 ? (
          <div className="text-center py-16 px-8 relative z-10">
            <div className="mb-8">
              <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-primary-500/20 to-secondary-500/20 rounded-full flex items-center justify-center border border-primary-500/30">
                <Award className="w-10 h-10 text-primary-400" />
              </div>
              <h3 className="text-xl font-bold text-text-enterprise-primary mb-2">
                No submissions found
              </h3>
              <p className="text-text-enterprise-secondary text-base">
                This candidate hasn't been submitted to any requirements yet
              </p>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto relative z-10">
            <table className="w-full">
              <thead className="sticky top-0 z-10 bg-gradient-to-r from-glass-primary/40 via-glass-secondary/35 to-glass-primary/40 border-b border-glass-border/50 backdrop-blur-xl">
                <tr>
                  <th className="px-6 py-4 text-left">
                    <div className="flex items-center gap-2">
                      <Building className="w-4 h-4 text-primary-400" />
                      <span className="text-sm font-bold text-text-enterprise-primary uppercase tracking-wider">
                        Requirement
                      </span>
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left">
                    <div className="flex items-center gap-2">
                      <BarChart3 className="w-4 h-4 text-primary-400" />
                      <span className="text-sm font-bold text-text-enterprise-primary uppercase tracking-wider">
                        Fitment Score
                      </span>
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-primary-400" />
                      <span className="text-sm font-bold text-text-enterprise-primary uppercase tracking-wider">
                        Submitted By
                      </span>
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-primary-400" />
                      <span className="text-sm font-bold text-text-enterprise-primary uppercase tracking-wider">
                        Created At
                      </span>
                    </div>
                  </th>
                  <th className="px-6 py-4 text-right">
                    <span className="text-sm font-bold text-text-enterprise-primary uppercase tracking-wider">
                      Actions
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-glass-border/30">
                {submissions.map((submission: CandidateSubmission) => (
                  <tr
                    key={submission.submission_id}
                    className="hover:bg-gradient-to-r hover:from-glass-surface/20 hover:via-glass-surface/30 hover:to-glass-surface/20 transition-all duration-300"
                  >
                    <td className="px-6 py-4">
                      <div className="min-w-0">
                        <div className="font-semibold text-text-enterprise-primary text-lg">
                          {submission.name || 'Unknown Requirement'}
                        </div>
                        <div className="text-sm text-text-enterprise-secondary">
                          ID: {submission.requirement_id}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <span
                          className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getFitmentScoreColor(
                            submission.fitment_score
                          )}`}
                        >
                          {submission.fitment_score}%
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-text-enterprise-primary">
                        {submission.submitted_by_name || submission.submitted_by || '—'}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-text-enterprise-secondary">
                        {formatDateTime(submission.submission_created_at)}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewAnalysis(submission.candidate_id, submission.requirement_id)}
                          className="p-2 text-text-enterprise-tertiary hover:text-primary-400 hover:bg-primary-500/10 rounded-lg transition-all duration-200 border border-transparent hover:border-primary-500/20"
                          title="View analysis"
                        >
                          <BarChart3 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {total > 0 && (
        <div className="mt-4 bg-glass-surface/60 rounded-xl p-4 border border-glass-border/50 shadow-glass">
          <div className="flex items-center justify-between">
            <div className="text-sm text-text-enterprise-secondary">
              Showing <span className="font-semibold text-text-enterprise-primary">{((currentPage - 1) * pageSize) + 1}</span> to{' '}
              <span className="font-semibold text-text-enterprise-primary">{Math.min(currentPage * pageSize, total)}</span> of{' '}
              <span className="font-semibold text-text-enterprise-primary">{total}</span> submissions
            </div>
            {totalPages > 1 && (
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-2"
                >
                  Previous
                </Button>
                <span className="px-4 py-2 text-sm text-text-enterprise-secondary bg-glass-surface/50 rounded-lg">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-2"
                >
                  Next
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
